import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Edit2, Lock, DollarSign, Weight, TrendingUp,
  Calendar, FileText, Package, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  PENDENTE:   { label: 'Pendente',   cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  IMPLANTADO: { label: 'Implantado', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  NF_EMITIDA: { label: 'NF Emitida', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  CANCELADO:  { label: 'Cancelado',  cls: 'bg-slate-100 text-slate-500 border-slate-300' },
  ATRASADO:   { label: 'Atrasado',   cls: 'bg-red-100 text-red-700 border-red-300' },
};

const brl = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmt = (v, c = 2) => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: c, maximumFractionDigits: c }).format(v || 0);
const toNum = (v) => { if (!v && v !== 0) return 0; if (typeof v === 'number') return v; return parseFloat(String(v).replace(/\./g, '').replace(',', '.')) || 0; };
const fmtDate = (d) => { if (!d) return '---'; return new Date(d + 'T00:00:00').toLocaleDateString('pt-BR', { timeZone: 'UTC' }); };
const fmtDateTime = (d) => {
  if (!d) return '---';
  return new Date(d).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};
const lbl = "text-xs font-bold uppercase tracking-wide text-slate-400 mb-1 block";

const resolveStatus = (p) => {
  if (!p) return 'PENDENTE';
  if (p.status === 'NF_EMITIDA' || p.status === 'CANCELADO') return p.status;
  if (p.data_entrega) {
    const hoje = new Date().toISOString().split('T')[0];
    if (p.data_entrega < hoje) return 'ATRASADO';
  }
  return p.status || 'PENDENTE';
};

const MOTIVOS = {
  'cliente_aceitou': 'Cliente aceitou',
  'fabrica_liquidou': 'Fábrica liquidou',
  'entrega_parcial': 'Entrega parcial',
};

const getVariacaoInfo = (variacao) => {
  const abs = Math.abs(parseFloat(variacao) || 0);
  if (abs <= 10) return { cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', semaforo: '🟢' };
  if (abs <= 20) return { cls: 'bg-orange-100 text-orange-800 border-orange-300', semaforo: '🟡' };
  return { cls: 'bg-red-100 text-red-800 border-red-300', semaforo: '🔴' };
};

export default function PedidoDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [pedido, setPedido] = useState(null);
  const [nf, setNf] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const { data: p } = await api.get(`/pedidos/${id}`);
        setPedido(p);
        if (p.nota_fiscal_id) {
          try {
            const { data: nfData } = await api.get(`/notas-fiscais/${p.nota_fiscal_id}`);
            setNf(nfData);
          } catch {}
        }
      } catch {
        toast.error('Pedido não encontrado');
        navigate('/pedidos');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-[80vh] gap-4">
      <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin" />
      <p className="text-xs font-black uppercase tracking-widest text-slate-400 animate-pulse">Carregando Pedido...</p>
    </div>
  );

  if (!pedido) return null;

  const item = pedido.itens?.[0] || {};
  const travado = pedido.status === 'NF_EMITIDA' && !isAdmin;
  const statusResolvido = resolveStatus(pedido);
  const statusCfg = STATUS_CONFIG[statusResolvido] || STATUS_CONFIG.PENDENTE;
  const comissaoValor = toNum(pedido.comissao_valor || item.comissao_valor || 0);
  const comissaoPercent = toNum(item.comissao_percent || 0);
  const pesoTon = toNum(pedido.peso_total) / 1000;

  // Normaliza itens para exibição — suporta pedidos antigos (sem array itens)
  const itensDisplay = pedido.itens && pedido.itens.length > 0
    ? pedido.itens.map((it, i) => {
        const qtde = parseInt(it.quantidade) || 0;
        const pesoTotal = toNum(it.peso_calculado);
        const pesoUnit = qtde > 0 ? pesoTotal / qtde : 0;
        const precoMilheiro = toNum(it.valor_unitario);
        const fator = it.fator ? toNum(it.fator) : (pesoUnit > 0 ? precoMilheiro / (pesoUnit * 1000) : 0);
        return {
          numero_fe: it.numero_fe || (i === 0 ? pedido.numero_fe : '') || '—',
          item_nome: it.item_nome || (i === 0 ? pedido.item_nome : '') || '—',
          quantidade: qtde,
          peso_total: pesoTotal,
          valor_unitario: precoMilheiro,
          subtotal: toNum(it.subtotal),
          comissao_percent: toNum(it.comissao_percent),
          comissao_valor: toNum(it.comissao_valor),
          fator,
        };
      })
    : [{
        numero_fe: pedido.numero_fe || '—',
        item_nome: pedido.item_nome || '—',
        quantidade: parseInt(pedido.quantidade) || 0,
        peso_total: toNum(pedido.peso_total),
        valor_unitario: toNum(item.valor_unitario),
        subtotal: toNum(pedido.valor_total),
        comissao_percent: toNum(item.comissao_percent),
        comissao_valor: toNum(item.comissao_valor || pedido.comissao_valor),
        fator: toNum(pedido.peso_total) > 0 ? toNum(pedido.valor_total) / toNum(pedido.peso_total) : 0,
      }];
  const h = pedido.historico_entrega;
  const variInfo = h ? getVariacaoInfo(h.variacao_percent) : null;

  const timelineEvents = [
    pedido.criado_em && {
      icon: Package,
      label: 'Pedido criado',
      date: fmtDateTime(pedido.criado_em),
      by: pedido.criado_por,
      color: 'text-blue-600 bg-blue-50 border-blue-200',
    },
    pedido.atualizado_em && {
      icon: Edit2,
      label: 'Atualizado',
      date: fmtDateTime(pedido.atualizado_em),
      by: pedido.atualizado_por,
      color: 'text-amber-600 bg-amber-50 border-amber-200',
    },
    nf && {
      icon: FileText,
      label: `NF ${nf.numero_nf} emitida`,
      date: fmtDate(nf.data_emissao),
      by: nf.criado_por,
      color: 'text-emerald-600 bg-emerald-50 border-emerald-200',
    },
    h && {
      icon: CheckCircle2,
      label: 'Entrega registrada',
      date: fmtDateTime(h.registrado_em),
      by: null,
      color: 'text-green-600 bg-green-50 border-green-200',
    },
  ].filter(Boolean);

  return (
    <div className="p-4 md:p-6 bg-[#E9EEF2] min-h-screen">

      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-3">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/pedidos')}
            className="flex items-center gap-1.5 text-xs font-black uppercase text-slate-500 hover:text-[#0A3D73] transition-colors">
            <ArrowLeft size={14} /> Voltar
          </button>
          <div className="w-px h-5 bg-slate-200" />
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-black text-slate-900 font-mono">
                {pedido.numero_fabrica || pedido.numero_oc || '---'}
              </h1>
              <span className={`px-2 py-0.5 text-[10px] font-black border rounded-full ${statusCfg.cls}`}>
                {statusCfg.label}
              </span>
              {pedido.pedido_mae && (
                <span className="px-2 py-0.5 text-[10px] font-black border rounded-full bg-blue-100 text-blue-700 border-blue-200">
                  SALDO
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500 font-semibold mt-0.5">{pedido.cliente_nome}</p>
          </div>
        </div>
        <button
          onClick={() => { if (!travado) navigate(`/pedidos/editar/${id}`); }}
          disabled={travado}
          className={`flex items-center gap-2 text-xs font-black uppercase px-4 h-9 rounded-lg shadow-sm transition-colors ${
            travado
              ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
              : 'bg-[#0A3D73] hover:bg-[#082D54] text-white'
          }`}>
          {travado ? <Lock size={13} /> : <Edit2 size={13} />}
          {travado ? 'NF Emitida' : 'Editar Pedido'}
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          {
            label: 'Valor Total',
            value: brl(pedido.valor_total),
            sub: null,
            icon: DollarSign,
            cor: '#166534',
            fundo: '#DCFCE7',
          },
          {
            label: 'Peso Total',
            value: `${fmt(pedido.peso_total)} KG`,
            sub: `${fmt(pesoTon, 3)} TON`,
            icon: Weight,
            cor: '#1E40AF',
            fundo: '#EFF6FF',
          },
          {
            label: 'Comissão',
            value: brl(comissaoValor),
            sub: comissaoPercent > 0 ? `${fmt(comissaoPercent)}%` : null,
            icon: TrendingUp,
            cor: '#92400E',
            fundo: '#FEF3C7',
          },
          {
            label: 'Data de Entrega',
            value: fmtDate(pedido.data_entrega),
            sub: null,
            icon: Calendar,
            cor: '#6B21A8',
            fundo: '#F3E8FF',
          },
        ].map((k, i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{k.label}</p>
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: k.fundo }}>
                <k.icon size={13} style={{ color: k.cor }} />
              </div>
            </div>
            <p className="text-lg font-black" style={{ color: k.cor }}>{k.value}</p>
            {k.sub && <p className="text-xs font-bold text-slate-400 mt-0.5">{k.sub}</p>}
          </div>
        ))}
      </div>

      {/* CORPO 2 COLUNAS */}
      <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-6">

        {/* COL ESQUERDA */}
        <div className="space-y-6">

          {/* Card Identificação */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">Identificação</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className={lbl}>Nº Pedido Fábrica</p>
                <p className="text-sm font-black font-mono text-slate-800">{pedido.numero_fabrica || '—'}</p>
              </div>
              <div>
                <p className={lbl}>FE</p>
                <p className="text-sm font-black font-mono text-blue-700">{pedido.numero_fe || '—'}</p>
              </div>
              <div>
                <p className={lbl}>OC Cliente</p>
                <p className="text-sm font-black font-mono text-slate-700">{pedido.numero_oc || '—'}</p>
              </div>
              <div className="col-span-2 md:col-span-3">
                <p className={lbl}>Cliente</p>
                <p className="text-sm font-bold text-slate-800 uppercase">{pedido.cliente_nome || '—'}</p>
              </div>
              <div>
                <p className={lbl}>Representada</p>
                {pedido.representada_nome
                  ? <span className="inline-flex items-center px-2.5 py-1 text-xs font-black rounded-lg bg-[#0A3D73]/10 text-[#0A3D73] border border-[#0A3D73]/20">{pedido.representada_nome}</span>
                  : <span className="text-sm text-slate-400 font-normal">—</span>
                }
              </div>
              <div className="col-span-2 md:col-span-3">
                <p className={lbl}>Referência</p>
                <p className="text-sm font-bold text-slate-700 uppercase">{pedido.item_nome || '—'}</p>
              </div>
              <div>
                <p className={lbl}>Criado em</p>
                <p className="text-xs font-bold text-slate-600">{fmtDateTime(pedido.criado_em)}</p>
                {pedido.criado_por && <p className="text-[10px] text-slate-400 font-semibold mt-0.5">{pedido.criado_por}</p>}
              </div>
              {pedido.atualizado_em && (
                <div>
                  <p className={lbl}>Atualizado em</p>
                  <p className="text-xs font-bold text-slate-600">{fmtDateTime(pedido.atualizado_em)}</p>
                  {pedido.atualizado_por && <p className="text-[10px] text-slate-400 font-semibold mt-0.5">{pedido.atualizado_por}</p>}
                </div>
              )}
            </div>
          </div>

          {/* Card Itens do Pedido */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">
              Itens do Pedido ({itensDisplay.length})
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-[#0A3D73] text-white text-[10px] font-black uppercase">
                    <th className="px-2 py-2 text-left rounded-tl-lg">FE</th>
                    <th className="px-2 py-2 text-left">Descrição</th>
                    <th className="px-2 py-2 text-right">Qtde</th>
                    <th className="px-2 py-2 text-right">Peso KG</th>
                    <th className="px-2 py-2 text-right">R$/Milh</th>
                    <th className="px-2 py-2 text-right">Fator</th>
                    <th className="px-2 py-2 text-right">Valor Total</th>
                    <th className="px-2 py-2 text-right">Com%</th>
                    <th className="px-2 py-2 text-right rounded-tr-lg">Com R$</th>
                  </tr>
                </thead>
                <tbody>
                  {itensDisplay.map((it, i) => (
                    <tr key={i} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}`}>
                      <td className="px-2 py-2.5 font-black text-blue-700 font-mono">{it.numero_fe}</td>
                      <td className="px-2 py-2.5 font-semibold text-slate-700">{it.item_nome}</td>
                      <td className="px-2 py-2.5 text-right font-bold">{(it.quantidade || 0).toLocaleString('pt-BR')}</td>
                      <td className="px-2 py-2.5 text-right font-bold text-blue-600">{fmt(it.peso_total)}</td>
                      <td className="px-2 py-2.5 text-right font-semibold">{it.valor_unitario > 0 ? brl(it.valor_unitario) : '—'}</td>
                      <td className="px-2 py-2.5 text-right font-semibold">{it.fator > 0 ? `R$ ${fmt(it.fator)}` : '—'}</td>
                      <td className="px-2 py-2.5 text-right font-black text-emerald-700">{brl(it.subtotal)}</td>
                      <td className="px-2 py-2.5 text-right font-semibold">{it.comissao_percent > 0 ? `${fmt(it.comissao_percent)}%` : '—'}</td>
                      <td className="px-2 py-2.5 text-right font-bold text-green-700">{brl(it.comissao_valor)}</td>
                    </tr>
                  ))}
                </tbody>
                {itensDisplay.length > 1 && (
                  <tfoot>
                    <tr className="bg-[#0A3D73] text-white font-black text-[10px]">
                      <td colSpan={2} className="px-2 py-2">{itensDisplay.length} itens</td>
                      <td className="px-2 py-2 text-right">
                        {itensDisplay.reduce((a, it) => a + (it.quantidade || 0), 0).toLocaleString('pt-BR')}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {fmt(itensDisplay.reduce((a, it) => a + toNum(it.peso_total), 0))}
                      </td>
                      <td colSpan={2} />
                      <td className="px-2 py-2 text-right text-emerald-300">
                        {brl(itensDisplay.reduce((a, it) => a + toNum(it.subtotal), 0))}
                      </td>
                      <td />
                      <td className="px-2 py-2 text-right text-green-300">
                        {brl(itensDisplay.reduce((a, it) => a + toNum(it.comissao_valor), 0))}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* Card Histórico de Entrega */}
          {h && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">Histórico de Entrega</p>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className={lbl}>Qtde Pedida</p>
                    <p className="text-sm font-black text-slate-800">{(h.qtde_pedida || 0).toLocaleString('pt-BR')}</p>
                  </div>
                  <div>
                    <p className={lbl}>Qtde Entregue</p>
                    <p className="text-sm font-black text-slate-800">{(h.qtde_entregue || 0).toLocaleString('pt-BR')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wide">Variação</span>
                  <span className={`flex items-center gap-1.5 px-3 py-1 text-sm font-black border rounded-full ${variInfo.cls}`}>
                    {variInfo.semaforo} {parseFloat(h.variacao_percent) >= 0 ? '+' : ''}{h.variacao_percent}%
                  </span>
                </div>
                {h.motivo && (
                  <div>
                    <p className={lbl}>Motivo</p>
                    <p className="text-sm font-bold text-slate-700">{MOTIVOS[h.motivo] || h.motivo}</p>
                  </div>
                )}
                {h.registrado_em && (
                  <div>
                    <p className={lbl}>Registrado em</p>
                    <p className="text-xs font-bold text-slate-600">{fmtDateTime(h.registrado_em)}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Card Observações */}
          {pedido.observacoes && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">Observações</p>
              <p className="text-sm font-semibold text-slate-700 whitespace-pre-wrap">{pedido.observacoes}</p>
            </div>
          )}

        </div>

        {/* COL DIREITA */}
        <div className="space-y-6">

          {/* Card Status do Pedido */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <p className="text-xs font-black uppercase tracking-widests text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">Status do Pedido</p>
            <div className="space-y-4">
              <div className="flex justify-center pt-1">
                <span className={`px-5 py-2 text-sm font-black border-2 rounded-xl ${statusCfg.cls}`}>
                  {statusCfg.label}
                </span>
              </div>
              <div>
                <p className={lbl}>Data de Entrega</p>
                <p className="text-sm font-black text-slate-800">{fmtDate(pedido.data_entrega)}</p>
              </div>
              {pedido.pedido_mae && (
                <div>
                  <p className={lbl}>Pedido Mãe</p>
                  <p className="text-sm font-black font-mono text-blue-700">{pedido.pedido_mae}</p>
                </div>
              )}
              {pedido.condicao_pagamento && (
                <div>
                  <p className={lbl}>Condição de Pagamento</p>
                  <p className="text-sm font-bold text-slate-700">{pedido.condicao_pagamento}</p>
                </div>
              )}
            </div>
          </div>

          {/* Card Nota Fiscal */}
          {nf && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4 flex items-center gap-2">
                <FileText size={13} /> Nota Fiscal
              </p>
              <div className="space-y-3">
                <div>
                  <p className={lbl}>Número NF</p>
                  <p className="text-2xl font-black font-mono text-[#0A3D73]">{nf.numero_nf}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className={lbl}>Data Emissão</p>
                    <p className="text-sm font-bold text-slate-700">{fmtDate(nf.data_emissao)}</p>
                  </div>
                  <div>
                    <p className={lbl}>Parcelas</p>
                    <p className="text-sm font-bold text-slate-700">{nf.numero_parcelas}x</p>
                  </div>
                </div>
                <div>
                  <p className={lbl}>Valor NF</p>
                  <p className="text-lg font-black text-emerald-700">{brl(nf.valor_total)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Card Linha do Tempo */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">Linha do Tempo</p>
            <div className="space-y-0">
              {timelineEvents.map((ev, i) => (
                <div key={i} className="flex gap-3 pb-4 relative">
                  {i < timelineEvents.length - 1 && (
                    <div className="absolute left-[13px] top-7 bottom-0 w-px bg-slate-200" />
                  )}
                  <div className={`w-7 h-7 rounded-full border flex items-center justify-center flex-shrink-0 ${ev.color}`}>
                    <ev.icon size={12} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-black text-slate-700">{ev.label}</p>
                    <p className="text-[10px] text-slate-400 font-semibold">{ev.date}</p>
                    {ev.by && <p className="text-[10px] text-slate-400">{ev.by}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
